import {useNavigate} from 'react-router-dom';
import {Screen} from '../types';
import {useFilters} from '../contexts';
import {breadcrumbStack} from '../utils/breadcrumbStack';
import {isTitleColumn} from '../utils/uiUtils';

/** Where the user came from, for the breadcrumb: the screen itself or the home page. */
interface Origin {
    label: string;
    /** Resolved at click time: a screen's path carries its current query string. */
    path: () => string;
}

/**
 * What a screen's table does on a row click or a create button: navigates to
 * the form or the target screen with the breadcrumb pushed. Shared by the
 * screen page and the screen widgets on the home page, so both behave alike.
 */
export const useScreenNavigation = (appId: string, screen: Screen, origin: Origin) => {
    const navigate = useNavigate();
    const {getFilter} = useFilters();

    const captureCurrentFilters = (): Record<string, string> => {
        const filters: Record<string, string> = {};
        screen.filters?.forEach(f => {
            const value = getFilter(f.code);
            if (value) filters[f.code] = value;
        });
        return filters;
    };

    const handleOpenCreateForm = (form: any, prefill?: Record<string, any>) => {
        const hasOneToMany = form.fields?.some((field: any) => field.type === 'one-to-many');
        const currentFilters = captureCurrentFilters();
        const dest = hasOneToMany
            ? `/app/${appId}/create-form/${form.id}`
            : `/app/${appId}/create-simple-form/${form.id}`;

        breadcrumbStack.push([{
            label: origin.label,
            path: origin.path(),
            filters: currentFilters
        }], dest);
        breadcrumbStack.markDrilldown();

        navigate(dest, prefill ? {state: {prefill}} : undefined);
    };

    const handleOpenUpdateForm = (form: any, rowData: any) => {
        const hasOneToMany = form.fields?.some((field: any) => field.type === 'one-to-many');
        const currentFilters = captureCurrentFilters();
        const dest = hasOneToMany
            ? `/app/${appId}/update-form/${form.id}/${rowData.id}`
            : `/app/${appId}/update-simple-form/${form.id}/${rowData.id}`;

        // Find row title for breadcrumb context
        const titleColumn = screen.columns?.find(isTitleColumn);
        const rawTitleValue = titleColumn ? rowData[titleColumn.code] : null;
        const titleValue = rawTitleValue && typeof rawTitleValue === 'object'
            ? rawTitleValue[titleColumn?.targetColumn?.columnName ?? 'name'] ?? ''
            : rawTitleValue;

        breadcrumbStack.push([
            { label: origin.label, path: origin.path(), filters: currentFilters },
            ...(titleValue ? [{ label: String(titleValue) }] : [])
        ], dest);
        breadcrumbStack.markDrilldown();

        navigate(dest, {state: {rowData}});
    };

    const handleNavigateToScreen = (newScreenId: string, titleValue?: string, previousFilters?: Record<string, string>) => {
        const dest = `/app/${appId}/screen/${newScreenId}`;

        breadcrumbStack.push([
            { label: origin.label, path: origin.path(), filters: previousFilters || captureCurrentFilters() },
            ...(titleValue ? [{ label: titleValue }] : [])
        ], dest);
        breadcrumbStack.markDrilldown();

        navigate(dest);
    };

    return {handleOpenCreateForm, handleOpenUpdateForm, handleNavigateToScreen};
};
